// The program's leaderboard on a Training's panel (Cian, 2026-10-16): back from
// the old program drawer (home/cards/Leaderboard.tsx), drawn as the panel's own
// ruled lines. Everyone in the program by progress, the top five and you.
// Data: GET /group/{grpId}/MenteesPeers, as the old drawer read it.

import React from 'react';
import { Box, Typography } from '@mui/material';
import useMenteePeers from '../../../../../queries/useMenteePeers';
import { getUserDetails } from '../../../../../utils/orgName';
import { capabilityColor } from '../../Program/components/learning-lifecycle/teamCapability';
import type { MemberProgramCardData } from '../../home/member-learning-lifecycle/types';
import { BODY, BRANDY, FONT, LABEL, LINE, TEXT, TEXT_3 } from '../designSystem';

const TOP = 5;
const RANK_MEDALS = ['🥇', '🥈', '🥉'];

interface Entry {
  id: string;
  name: string;
  progress: number;
  quiz: number | null;
  you: boolean;
}

const num = (v: unknown): number | null => {
  const n = typeof v === 'string' ? Number(v) : v;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
};
// The progress figure, wherever the payload puts it (the old drawer's resolveGoalProgress).
const progressOf = (m: any): number =>
  num(m?.goalProgress) ??
  num(m?.progress?.goalProgress) ??
  num(m?.progress?.progress) ??
  num(m?.progress) ??
  num(m?.progressPercentage) ??
  num(m?.progressPercent) ??
  0;
const pct = (v: number) => Math.round(Math.min(Math.max(v, 0), 100));

const head = (label: string, width?: number) => (
  <Typography sx={{ ...LABEL, fontSize: 9.5, width, flexShrink: 0, textAlign: width ? 'right' : 'left' }}>
    {label}
  </Typography>
);

const ProgramLeaderboard: React.FC<{ program: MemberProgramCardData }> = ({ program }) => {
  const { orgId, location, userId } = getUserDetails();
  const { data, isLoading, isError } = useMenteePeers({ grpId: program.id, orgId, location });

  const payload = (data as any)?.data?.data ?? (data as any)?.data ?? [];
  const peers: any[] = (Array.isArray(payload) ? payload : []).flatMap((item: any) =>
    Array.isArray(item?.menteeDetails) ? item.menteeDetails : []
  );
  const ranked: Entry[] = peers
    .map((m) => {
      const id = String(m?.mid || m?.userId || m?.id || '');
      return {
        id,
        name: m?.displayName || m?.name || 'Unnamed',
        progress: pct(progressOf(m)),
        quiz: num(m?.MCQScore),
        you: !!userId && id === userId
      };
    })
    .sort((a, b) => b.progress - a.progress);
  // The learner is always on the board, with their own figures when the program's list leaves them out.
  if (userId && !ranked.some((e) => e.you)) {
    ranked.push({ id: userId, name: 'You', progress: pct(program.goalProgress), quiz: program.mcqScore, you: true });
    ranked.sort((a, b) => b.progress - a.progress);
  }
  const youAt = ranked.findIndex((e) => e.you);
  const shown = ranked.slice(0, TOP).map((e, i) => ({ ...e, rank: i + 1 }));
  if (youAt >= TOP) shown.push({ ...ranked[youAt], rank: youAt + 1 });

  return (
    <Box>
      <Typography sx={{ ...LABEL, mb: '8px' }}>Program Leaderboard</Typography>
      {isLoading ? (
        <Typography sx={{ ...BODY, fontSize: 12.5, color: TEXT_3 }}>Loading the leaderboard...</Typography>
      ) : isError ? (
        <Typography sx={{ ...BODY, fontSize: 12.5, color: TEXT_3 }}>The leaderboard couldn’t be loaded.</Typography>
      ) : shown.length === 0 ? (
        <Typography sx={{ ...BODY, fontSize: 12.5, color: TEXT_3 }}>No one has started yet.</Typography>
      ) : (
        <Box sx={{ borderTop: `1px solid ${LINE}` }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              minHeight: 28,
              borderBottom: `1px solid ${LINE}`
            }}
          >
            {head('#', 24)}
            <Box sx={{ flex: 1 }}>{head('Name')}</Box>
            {head('Progress', 64)}
            {head('Quiz', 48)}
          </Box>
          {shown.map((e) => (
            <Box
              key={`${e.id}-${e.rank}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                minHeight: 38,
                borderBottom: `1px solid ${LINE}`,
                ...(e.you ? { bgcolor: 'rgba(247,148,32,0.06)' } : {})
              }}
            >
              <Typography
                sx={{ width: 24, flexShrink: 0, textAlign: 'right', fontFamily: FONT, fontSize: 13, color: TEXT_3 }}
              >
                {e.rank <= RANK_MEDALS.length ? RANK_MEDALS[e.rank - 1] : e.rank}
              </Typography>
              <Typography
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontFamily: FONT,
                  fontSize: 13,
                  fontWeight: e.you ? 700 : 400,
                  color: e.you ? BRANDY : TEXT,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {e.you && e.name !== 'You' ? `${e.name} (You)` : e.name}
              </Typography>
              <Typography
                sx={{
                  width: 64,
                  flexShrink: 0,
                  textAlign: 'right',
                  fontFamily: FONT,
                  fontSize: 12,
                  fontWeight: 700,
                  color: capabilityColor(e.progress)
                }}
              >
                {e.progress}%
              </Typography>
              <Typography
                sx={{ width: 48, flexShrink: 0, textAlign: 'right', fontFamily: FONT, fontSize: 12, color: TEXT_3 }}
              >
                {e.quiz === null ? '-' : `${Math.round(e.quiz)}%`}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ProgramLeaderboard;
